import type { Tier, BillingPeriod } from "@mw/shared/pricing";
import { TIERS, getPricingTiers, getSubscriptionPrice } from "@mw/shared/pricing";
import type { StaticImageData } from "next/image";

import { getBusinessMaxMinutes } from "./PricingCalculator/calculatorUtils";
import {
  FlashCover,
  MultilingualCover,
  ScribeCover,
  ScribeRealtimeCover,
  MusicCover,
  VoiceChangerCover,
  VoiceIsolatorCover,
  SFXCover,
  DubbingCover,
} from "./covers";

// ---------------------------------------------------------------------------
// Per-product PAYG pricing -- single source of truth from the PAYG tier
// ---------------------------------------------------------------------------

const paygTier = TIERS.payg;

/**
 * Per-product native USD rates, derived from the PAYG V2 tier's platformConfig.
 * Each product is expressed in its natural pricing unit:
 *  - TTS: USD per 1,000 characters
 *  - STT: USD per hour
 *  - Other audio products: USD per minute
 */
function getPaygNativeRates() {
  const api = paygTier.platformConfig.api;
  return {
    ttsMultilingualPer1KChars: api.textToSpeech.multilingualUsdPer1000Chars,
    ttsFlashPer1KChars: api.textToSpeech.turboUsdPer1000Chars,
    sttScribePerHour: api.speechToText.scribeUsdPerHour,
    sttScribeRealtimePerHour: api.speechToText.scribeRealtimeUsdPerHour,
    musicPerMinute: api.music.usdPerMinute,
    voiceIsolatorPerMinute: api.voiceIsolator.usdPerMinute,
    voiceChangerPerMinute: api.voiceChanger.usdPerMinute,
    soundEffectsPerMinute: api.soundEffects.usdPerMinute,
    dubbingPerMinute: api.dubbing.autoUsdPerMinute,
  };
}

// ---------------------------------------------------------------------------
// Per-product options for the PAYG cost calculator
// ---------------------------------------------------------------------------

export type PaygProductId =
  | "tts_flash"
  | "tts_multilingual"
  | "stt_scribe"
  | "stt_realtime"
  | "music"
  | "voice_isolator"
  | "voice_changer"
  | "sound_effects"
  | "dubbing";

export type { PaygUnit } from "./PricingCalculator/calculatorUtils";
import type { PaygUnit } from "./PricingCalculator/calculatorUtils";

export interface PaygProductOption {
  id: PaygProductId;
  unit: PaygUnit;
  /** USD per native unit. For "characters" this is per 1,000 characters. */
  pricePerUnit: number;
  /** Slider ceiling, in `unit`. */
  maxValue: number;
  /** Sensible default when first selecting this product. */
  defaultValue: number;
  /** Cover image rendered on the calculator's PAYG result card. */
  coverImage: StaticImageData;
}

// Aligned with the subscription calculator's Business-tier ceiling (~12K min = 6M credits / 500 cpm).
const MAX_MINUTES = getBusinessMaxMinutes(TIERS);
const DEFAULT_MINUTES = 50;
// Mirror the minutes ceiling at the industry-standard 1K chars / minute of generated speech.
const MAX_CHARACTERS = MAX_MINUTES * 1000;
const DEFAULT_CHARACTERS = 50_000;
const MAX_HOURS = 10_000;
const DEFAULT_HOURS = 50;

const COVER_IMAGES: Record<PaygProductId, StaticImageData> = {
  tts_flash: FlashCover,
  tts_multilingual: MultilingualCover,
  stt_scribe: ScribeCover,
  stt_realtime: ScribeRealtimeCover,
  music: MusicCover,
  voice_isolator: VoiceIsolatorCover,
  voice_changer: VoiceChangerCover,
  sound_effects: SFXCover,
  dubbing: DubbingCover,
};

/** Default product surfaced when the calculator first renders. */
export const DEFAULT_PAYG_PRODUCT_ID: PaygProductId = "tts_flash";

export const PAYG_PRODUCT_OPTIONS: PaygProductOption[] = (() => {
  const rates = getPaygNativeRates();
  const minutesProduct = (id: PaygProductId, pricePerUnit: number): PaygProductOption => ({
    id,
    unit: "minutes",
    pricePerUnit,
    maxValue: MAX_MINUTES,
    defaultValue: DEFAULT_MINUTES,
    coverImage: COVER_IMAGES[id],
  });
  const charactersProduct = (id: PaygProductId, pricePerUnit: number): PaygProductOption => ({
    id,
    unit: "characters",
    pricePerUnit,
    maxValue: MAX_CHARACTERS,
    defaultValue: DEFAULT_CHARACTERS,
    coverImage: COVER_IMAGES[id],
  });
  const hoursProduct = (id: PaygProductId, pricePerUnit: number): PaygProductOption => ({
    id,
    unit: "hours",
    pricePerUnit,
    maxValue: MAX_HOURS,
    defaultValue: DEFAULT_HOURS,
    coverImage: COVER_IMAGES[id],
  });

  return [
    charactersProduct("tts_flash", rates.ttsFlashPer1KChars),
    charactersProduct("tts_multilingual", rates.ttsMultilingualPer1KChars),
    hoursProduct("stt_scribe", rates.sttScribePerHour),
    hoursProduct("stt_realtime", rates.sttScribeRealtimePerHour),
    minutesProduct("music", rates.musicPerMinute),
    minutesProduct("voice_isolator", rates.voiceIsolatorPerMinute),
    minutesProduct("voice_changer", rates.voiceChangerPerMinute),
    minutesProduct("sound_effects", rates.soundEffectsPerMinute),
    minutesProduct("dubbing", rates.dubbingPerMinute),
  ];
})();

const PAYG_PRODUCT_BY_ID: Record<PaygProductId, PaygProductOption> = Object.fromEntries(
  PAYG_PRODUCT_OPTIONS.map(p => [p.id, p]),
) as Record<PaygProductId, PaygProductOption>;

/** O(1) lookup for a PAYG product by id. Falls back to the default product if `id` is unknown. */
export function getPaygProduct(id: PaygProductId): PaygProductOption {
  return PAYG_PRODUCT_BY_ID[id] ?? PAYG_PRODUCT_BY_ID[DEFAULT_PAYG_PRODUCT_ID];
}

/**
 * Compute estimated USD cost for a given usage value in a product's native unit.
 * For TTS the native unit is per 1,000 characters, so divide by 1,000.
 */
export function computePaygCostUsd(product: PaygProductOption, usage: number): number {
  if (product.unit === "characters") return (usage * product.pricePerUnit) / 1000;
  return usage * product.pricePerUnit;
}

// ---------------------------------------------------------------------------
// Subscription tier recommendation for an API workload
// ---------------------------------------------------------------------------

/**
 * Value-equivalent units of `product` for `tier`'s subscription dollars at PAYG rates.
 *
 * This is a "would you save vs PAYG?" heuristic — NOT the literal included credit
 * allowance. It answers: "if you spent the subscription's monthly price at PAYG
 * rates, how many units of `product` would you get?" The recommended-tier picker
 * uses this to find the cheapest plan whose dollar value beats the user's PAYG
 * cost. Real per-product included amounts vary across tiers (see tier limits in
 * shared/pricing.ts) and don't map cleanly to PAYG units for every product
 * (e.g. SFX is per-generation in tiers, per-minute in PAYG).
 */
function tierPaygValueUnits(tier: Tier, product: PaygProductOption, period: BillingPeriod): number | undefined {
  const subscriptionPrice = getSubscriptionPrice(tier, period);
  if (!subscriptionPrice || product.pricePerUnit <= 0) return undefined;
  const raw = subscriptionPrice / product.pricePerUnit;
  return product.unit === "characters" ? raw * 1000 : raw;
}

export interface SubscriptionRecommendation {
  /** Smallest tier whose subscription dollars beat the user's PAYG cost at this usage. */
  tier: Tier;
  /** Subscription price for the active billing period (undefined for enterprise). */
  monthlyPrice: number | undefined;
  /** PAYG-equivalent units of `product` for this tier's subscription dollars. See tierPaygValueUnits. */
  paygValueUnits: number | undefined;
  isEnterprise: boolean;
}

/**
 * Pick the smallest non-enterprise tier whose PAYG-equivalent value at this product's
 * rate covers `usage`. Falls back to Enterprise if no tier covers it.
 */
export function recommendSubscriptionTier(
  product: PaygProductOption,
  usage: number,
  period: BillingPeriod,
): SubscriptionRecommendation {
  const tiers = getPricingTiers();
  const candidates = tiers
    .filter(t => t.planId !== "enterprise" && t.planId !== "payg" && t.planId !== "free")
    .map(tier => ({
      tier,
      paygValueUnits: tierPaygValueUnits(tier, product, period),
      monthlyPrice: getSubscriptionPrice(tier, period),
    }))
    .filter((c): c is typeof c & { monthlyPrice: number } => c.monthlyPrice !== undefined)
    .sort((a, b) => a.monthlyPrice - b.monthlyPrice);

  const fit = candidates.find(c => c.paygValueUnits !== undefined && c.paygValueUnits >= usage);
  if (fit) {
    return {
      tier: fit.tier,
      monthlyPrice: fit.monthlyPrice,
      paygValueUnits: fit.paygValueUnits,
      isEnterprise: false,
    };
  }

  const enterprise = tiers.find(t => t.planId === "enterprise");
  return {
    tier: enterprise ?? candidates[candidates.length - 1].tier,
    monthlyPrice: undefined,
    paygValueUnits: undefined,
    isEnterprise: enterprise !== undefined,
  };
}
